<template>
    <!-- Vimeo lazy loading with thumbnail (only when lazy=true and is Vimeo) -->
    <div v-if="shouldLazyLoad && !iframeLoaded" class="vimeo-lazy-container" :style="containerStyle">
        <div v-if="loadingMetadata" class="vimeo-loading">
            {{ trans('Loading...') }}
        </div>
        <div v-else-if="vimeoMetadata && vimeoMetadata.thumbnail_url"
             class="vimeo-thumbnail-wrapper"
             @click="loadIframe">
            <img :src="vimeoMetadata.thumbnail_url"
                 :alt="vimeoMetadata.title || 'Video thumbnail'"
                 class="vimeo-thumbnail">
            <div class="vimeo-play-overlay">
                <svg class="vimeo-play-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="50" r="50" fill="rgba(0, 0, 0, 0.7)"/>
                    <polygon points="40,30 40,70 70,50" fill="white"/>
                </svg>
            </div>
        </div>
        <div v-else-if="vimeoMetadata && vimeoMetadata.thumbnail_url"
             class="vimeo-thumbnail-wrapper"
             @click="loadIframe">
            <img :src="vimeoMetadata.thumbnail_url"
                 :alt="vimeoMetadata.title || 'Video thumbnail'"
                 class="vimeo-thumbnail">
            <div class="vimeo-play-overlay">
                <svg class="vimeo-play-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="50" r="50" fill="rgba(0, 0, 0, 0.7)"/>
                    <polygon points="40,30 40,70 70,50" fill="white"/>
                </svg>
            </div>
        </div>
        <div v-else class="vimeo-error">
            {{ trans('Unable to load video preview') }}
        </div>
    </div>
    <!-- Regular iframe (old version - when lazy=false or not Vimeo or after click) -->
    <iframe v-else
            class="quiz-custom-iframe"
            :width="this.currentWidth" :height="this.currentHeight"
            :src="convertUrl()"
            title="YouTube video player" frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen></iframe>
</template>

<script>
export default {
    name: "CustomIframe",
    props: {
        url: {
            type: String,
            required: true
        },
        width: {
            type: String,
            default: '100%'
        },
        height: {
            type: String,
            default: '300px'
        },
        title: {
            type: String,
            default: 'Video player'
        },
        allow: {
            type: String,
            default: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        },
        allowfullscreen: {
            type: Boolean,
            default: true
        },
        lazy: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isVimeoUrl: false,
            loadingMetadata: false,
            vimeoMetadata: null,
            iframeLoaded: false,
            errorLoadingMetadata: false
        }
    },
    computed: {
      containerStyle() {
        return {
          width: this.currentWidth,
          height: this.currentHeight,
          marginInline: 'auto',
          position: 'relative',
          cursor: 'pointer'
        };
      },

      currentWidth() {
        return (this.vimeoMetadata && this.vimeoMetadata.width) ? this.vimeoMetadata.width + 'px' : this.width;
      },
      currentHeight() {
        return (this.vimeoMetadata && this.vimeoMetadata.height) ? this.vimeoMetadata.height + 'px' : this.height;
      },

      shouldLazyLoad() {
            return this.lazy && this.isVimeoUrl;
        }
    },
    mounted() {
        this.checkIfVimeo();
        if (this.shouldLazyLoad) {
            // Only fetch metadata if lazy is enabled and it's a Vimeo URL
            this.fetchVimeoMetadata();
        } else {
            // For non-lazy or non-Vimeo URLs, load iframe immediately (old behavior)
            this.iframeLoaded = true;
        }
    },
    methods: {
        checkIfVimeo() {
            if (!this.url) {
                this.isVimeoUrl = false;
                return;
            }
            this.isVimeoUrl = this.url.includes('vimeo.com');
        },
        async fetchVimeoMetadata() {
            if (!this.isVimeoUrl) return;

            this.loadingMetadata = true;
            this.errorLoadingMetadata = false;

            try {
                const apiUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(this.url)}`;
                const response = await fetch(apiUrl);

                if (!response.ok) {
                    throw new Error('Failed to fetch Vimeo metadata');
                }

                this.vimeoMetadata = await response.json();
            } catch (error) {
                console.error('Error fetching Vimeo metadata:', error);
                this.errorLoadingMetadata = true;
            } finally {
                this.loadingMetadata = false;
            }
        },
        loadIframe() {
            this.iframeLoaded = true;
        },
        convertUrl() {
            if (!this.url) return '';

            // YouTube detection
            if (this.url.includes('youtube') || this.url.includes('youtu.be')) {
                const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                const match = this.url.match(regExp);

                if (match && match[2]) {
                    return '//www.youtube.com/embed/' + match[2];
                }
            }

            // Vimeo detection
            if (this.url.includes('vimeo.com')) {
                const vimeoRegExp = /vimeo\.com\/(\d+)/;
                const match = this.url.match(vimeoRegExp);
                if (match && match[1]) {
                    // Add autoplay parameter if lazy loading was used and iframe is now loaded
                    const autoplayParam = (this.shouldLazyLoad && this.iframeLoaded) ? '?autoplay=1' : '';
                    return `//player.vimeo.com/video/${match[1]}${autoplayParam}`;
                }
            }

            return this.url;
        },
    }
}
</script>

<style scoped>
.vimeo-lazy-container {
    position: relative;
    background-color: #000;
    border-radius: 4px;
    overflow: hidden;
  max-width: 100%;
}

.vimeo-thumbnail-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.vimeo-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.vimeo-play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.3);
    transition: background-color 0.3s ease;
}

.vimeo-thumbnail-wrapper:hover .vimeo-play-overlay {
    background-color: rgba(0, 0, 0, 0.5);
}

.vimeo-play-icon {
    width: 80px;
    height: 80px;
    transition: transform 0.2s ease;
}

.vimeo-thumbnail-wrapper:hover .vimeo-play-icon {
    transform: scale(1.1);
}

.vimeo-loading,
.vimeo-error {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #fff;
    font-size: 14px;
}

.vimeo-error {
    color: #ff6b6b;
}

.quiz-custom-iframe{
  display: block;
  margin-inline: auto;
  max-width: 100%;
}

</style>
